import { authenticatedCacheGeneration } from '@/shared/lib/authenticated-session';

export type RuntimeModuleTransform = {
  position: [number, number, number];
  scale: [number, number, number];
};

export type RuntimeModuleState = {
  transforms: Record<string, RuntimeModuleTransform>;
  notes: Record<string, string>;
};

function sortRecord<T>(value: Record<string, T>): Record<string, T> {
  return Object.fromEntries(Object.entries(value).sort(([a], [b]) => a.localeCompare(b)));
}

export function moduleStateFingerprint(state: RuntimeModuleState): string {
  return JSON.stringify({
    transforms: sortRecord(state.transforms ?? {}),
    notes: sortRecord(state.notes ?? {}),
  });
}

const NOOP_TRANSFORM_EPSILON = 1e-6;

export function isNoopModuleTransform(transform: RuntimeModuleTransform): boolean {
  const values = [...transform.position, ...transform.scale];
  if (!values.every(Number.isFinite)) return false;
  return transform.position.every((value) => Math.abs(value) <= NOOP_TRANSFORM_EPSILON)
    && transform.scale.every((value) => Math.abs(value - 1) <= NOOP_TRANSFORM_EPSILON);
}

/**
 * Viewer gizmos may publish a transform on pointer-up even when the module did
 * not actually move.  Identity transforms are not runtime edits and must not
 * put a module-state save in front of a canonical resize command.
 */
export function normalizeEditableModuleState(
  transforms: Record<string, RuntimeModuleTransform>,
  notes: Record<string, string>,
): RuntimeModuleState {
  const sortedTransforms: Record<string, RuntimeModuleTransform> = {};
  const sortedNotes: Record<string, string> = {};
  for (const moduleId of Object.keys(transforms).sort()) {
    const transform = transforms[moduleId]!;
    const normalized = {
      position: transform.position,
      scale: [1, 1, 1] as [number, number, number],
    };
    if (!isNoopModuleTransform(normalized)) sortedTransforms[moduleId] = normalized;
  }
  for (const moduleId of Object.keys(notes).sort()) sortedNotes[moduleId] = notes[moduleId]!;
  return { transforms: sortedTransforms, notes: sortedNotes };
}

export function shouldFlushRuntimeStateBeforeCanonicalCommand(
  currentSnapshot: string,
  lastSavedSnapshot: string,
): boolean {
  // Zustand updates synchronously, while the React effect which calls
  // beginEdit() runs later. The concrete snapshot therefore owns this gate:
  // a user may edit X/Z and click resize in the same render turn while the
  // tracker still reports clean.
  return currentSnapshot !== lastSavedSnapshot;
}

export function planRuntimeStateBeforeCanonicalCommand(args: {
  transforms: Record<string, RuntimeModuleTransform>;
  notes: Record<string, string>;
  lastSavedSnapshot: string;
  activeModuleId: string;
}): {
  normalized: RuntimeModuleState;
  snapshot: string;
  flushRequired: boolean;
  dropActiveIdentity: boolean;
} {
  const normalized = normalizeEditableModuleState(args.transforms, args.notes);
  const snapshot = JSON.stringify(normalized);
  const activeTransform = args.transforms[args.activeModuleId];
  return {
    normalized,
    snapshot,
    flushRequired: shouldFlushRuntimeStateBeforeCanonicalCommand(
      snapshot,
      args.lastSavedSnapshot,
    ),
    dropActiveIdentity: Boolean(activeTransform && isNoopModuleTransform(activeTransform)),
  };
}

export async function flushRuntimeStateForCanonicalCommand(
  plan: { flushRequired: boolean },
  tracker: ModuleEditCasTracker,
  flush: () => Promise<unknown>,
): Promise<boolean> {
  if (!plan.flushRequired) return false;
  // Freeze the current revision synchronously, closing the window before the
  // autosave effect has observed a real move already present in the store.
  tracker.beginEdit();
  await flush();
  return true;
}

export function bundleRevisionKey(
  modelId: string,
  bundle: { bundleVersion?: number; bundleHash?: string },
): string {
  return `${modelId}:${Number(bundle.bundleVersion ?? 0)}:${String(bundle.bundleHash ?? '')}`;
}

export function canApplyNewServerState(
  currentState: RuntimeModuleState,
  lastAppliedServerFingerprint: string | null,
): boolean {
  return lastAppliedServerFingerprint === null
    || moduleStateFingerprint(currentState) === lastAppliedServerFingerprint;
}

export const MODULE_STATE_COMMITTED_EVENT = 'holzcom-module-state-committed';
export const MODULE_STATE_CONFLICT_EVENT = 'holzcom-module-state-conflict';
export const MODULE_COMMAND_COMMITTED_EVENT = 'holzcom-module-command-committed';
export const MODULE_STATE_SAVE_STARTED_EVENT = 'holzcom-module-state-save-started';
export const MODULE_STATE_SAVE_SETTLED_EVENT = 'holzcom-module-state-save-settled';
export const MODULE_COMMAND_TRACE_EVENT = 'holzcom-module-command-trace';
export type ModuleCommandTracePhase =
  | 'queued'
  | 'dequeued'
  | 'request_started'
  | 'fetch_dispatched'
  | 'response_received'
  | 'settled'
  | 'error';

export function announceModuleCommandTrace(
  modelId: string,
  operation: string,
  phase: ModuleCommandTracePhase,
): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(MODULE_COMMAND_TRACE_EVENT, {
    detail: { modelId, operation, phase, at: performance.now() },
  }));
}

export function committedBundleVersion(payload: unknown): number | null {
  if (!payload || typeof payload !== 'object') return null;
  const value = payload as Record<string, unknown>;
  const candidates = [
    value.bundleVersion,
    (value.model as Record<string, unknown> | undefined)?.bundleVersion,
  ];
  for (const candidate of candidates) {
    const version = Number(candidate);
    if (Number.isInteger(version) && version >= 0) return version;
  }
  return null;
}

export function announceOwnModuleCommand(modelId: string, payload: unknown): boolean {
  const bundleVersion = committedBundleVersion(payload);
  if (bundleVersion === null) return false;
  window.dispatchEvent(new CustomEvent(MODULE_COMMAND_COMMITTED_EVENT, {
    detail: { modelId, bundleVersion, authGeneration: authenticatedCacheGeneration() },
  }));
  return true;
}

export class ModuleEditCasTracker {
  private latestServerVersion: number;
  private editBaseVersion: number | null = null;

  constructor(serverVersion = 0) {
    this.latestServerVersion = serverVersion;
  }

  beginEdit(): number {
    if (this.editBaseVersion === null) this.editBaseVersion = this.latestServerVersion;
    return this.editBaseVersion;
  }

  observeRemote(serverVersion: number): boolean {
    this.latestServerVersion = Math.max(this.latestServerVersion, serverVersion);
    return this.editBaseVersion !== null && serverVersion > this.editBaseVersion;
  }

  committed(serverVersion: number): void {
    this.latestServerVersion = Math.max(this.latestServerVersion, serverVersion);
    this.editBaseVersion = null;
  }

  adoptOwnCommand(serverVersion: number): boolean {
    if (this.editBaseVersion !== null) return false;
    this.committed(serverVersion);
    return true;
  }

  reset(serverVersion: number): void {
    this.latestServerVersion = serverVersion;
    this.editBaseVersion = null;
  }

  expectedVersion(): number {
    return this.editBaseVersion ?? this.latestServerVersion;
  }

  get dirty(): boolean {
    return this.editBaseVersion !== null;
  }
}

/** Prevents a post-render debounce from duplicating an explicit state save.
 * Suppression is exact-snapshot scoped: the next different local edit is
 * eligible for autosave and is never silently rebased or discarded. */
export class ExplicitModuleStateSaveGate {
  private generation = 0;
  private snapshots = new Set<string>();

  begin(snapshot: string): number {
    this.generation += 1;
    this.snapshots = new Set([snapshot]);
    return this.generation;
  }

  shouldSchedule(snapshot: string): boolean {
    // React can render the pre-bake and post-bake command snapshots more than
    // once and in either order around query invalidation. Keep both suppressed
    // for the whole command generation; the first genuinely different local
    // fingerprint below ends that generation.
    if (this.snapshots.has(snapshot)) return false;
    this.snapshots.clear();
    return true;
  }

  failed(generation: number): void {
    if (generation === this.generation) this.snapshots.clear();
  }

  ownCommandCommitted(fallbackSnapshot: string): void {
    this.generation += 1;
    // A canonical command may commit before React has rendered the snapshot
    // which an explicit pre-command flush already saved. Preserve that exact
    // suppression; replacing it with the closure/render snapshot would let a
    // delayed duplicate autosave leave on the command's now-stale CAS base.
    if (this.snapshots.size === 0) this.snapshots.add(fallbackSnapshot);
  }

  reconcileCommittedSnapshot(snapshot: string): void {
    // A bake acknowledgement deliberately changes the local representation
    // from the flushed runtime transform to an empty transform map. Both are
    // products of the same command, not two user edits, so suppress each exact
    // fingerprint once. A later different fingerprint remains schedulable.
    this.snapshots.add(snapshot);
  }

  reset(): void {
    this.generation += 1;
    this.snapshots.clear();
  }
}
